import { ChangeDetectionStrategy, Component } from '@angular/core';
import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';

import { ERadioGroupType } from '@ui/sp-radio-group';
import {
    type CartSettingsFormGroupType,
    type CartSettingsFormType,
} from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import type { ESize } from '@common/enums';
import { EFontStyle, ELineStyle } from '@common/enums';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { FormArray, FormControl } from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { NgxIntlTelInputModule } from '@moddi3/ngx-intl-tel-input';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';

import { SpSwitchComponent } from '@ui/sp-switch';
import { SpRadioGroupComponent } from '@ui/sp-radio-group';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

@Component({
    selector: 'web-shop-cart-style-settings',
    templateUrl: './shop-cart-style-settings.component.html',
    styleUrls: ['./shop-cart-style-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        SpRadioGroupComponent,
        SpSwitchComponent,
        SpInputNumComponent,
        FormsModule,
        NgxIntlTelInputModule,
        ReactiveFormsModule,
        WebColorPickerComponent,
        TranslateModule,
    ],
})
export class ShopCartStyleSettingsComponent extends WebBaseControlTypedSettingsComponent<CartSettingsFormGroupType> {
    protected readonly ERadioGroupType = ERadioGroupType;
    protected readonly ELineStyle = ELineStyle;
    protected readonly EFontStyle = EFontStyle;

    public readonly SIZE_MIN = 0;
    public readonly SIZE_MAX = 100;
    public readonly FONT_SIZE_MAX = 72;
    public readonly BORDER_RADIUS_MIN: number = 0;
    public readonly BORDER_RADIUS_MAX: number = 250;

    public get previewSize(): CartSettingsFormType['previewSize'] {
        return this.itemForm.get('previewSize') as CartSettingsFormType['previewSize'];
    }

    public get countButtonSize(): CartSettingsFormType['countButtonSize'] {
        return this.itemForm.get('countButtonSize') as CartSettingsFormType['countButtonSize'];
    }

    public get isSeparatedItems(): CartSettingsFormType['isSeparatedItems'] {
        return this.itemForm.get('isSeparatedItems') as CartSettingsFormType['isSeparatedItems'];
    }

    public get separatorWidth(): CartSettingsFormType['separatorWidth'] {
        return this.itemForm.get('separatorWidth') as CartSettingsFormType['separatorWidth'];
    }

    public get separatorType(): CartSettingsFormType['separatorType'] {
        return this.itemForm.get('separatorType') as CartSettingsFormType['separatorType'];
    }

    public get separatorColor(): CartSettingsFormType['separatorColor'] {
        return this.itemForm.get('separatorColor') as CartSettingsFormType['separatorColor'];
    }

    public get itemTitleFontSize(): CartSettingsFormType['itemTitleFontSize'] {
        return this.itemForm.get('itemTitleFontSize') as CartSettingsFormType['itemTitleFontSize'];
    }

    public get itemTitleFontStyle(): CartSettingsFormType['itemTitleFontStyle'] {
        return this.itemForm.get('itemTitleFontStyle') as CartSettingsFormType['itemTitleFontStyle'];
    }

    public get itemTitleFontColor(): CartSettingsFormType['itemTitleFontColor'] {
        return this.itemForm.get('itemTitleFontColor') as CartSettingsFormType['itemTitleFontColor'];
    }

    public get itemPropertyFontSize(): CartSettingsFormType['itemPropertyFontSize'] {
        return this.itemForm.get('itemPropertyFontSize') as CartSettingsFormType['itemPropertyFontSize'];
    }

    public get itemPropertyFontStyle(): CartSettingsFormType['itemPropertyFontStyle'] {
        return this.itemForm.get('itemPropertyFontStyle') as CartSettingsFormType['itemPropertyFontStyle'];
    }

    public get itemPropertyFontColor(): CartSettingsFormType['itemPropertyFontColor'] {
        return this.itemForm.get('itemPropertyFontColor') as CartSettingsFormType['itemPropertyFontColor'];
    }

    public get itemPriceFontSize(): CartSettingsFormType['itemPriceFontSize'] {
        return this.itemForm.get('itemPriceFontSize') as CartSettingsFormType['itemPriceFontSize'];
    }

    public get itemPriceFontStyle(): CartSettingsFormType['itemPriceFontStyle'] {
        return this.itemForm.get('itemPriceFontStyle') as CartSettingsFormType['itemPriceFontStyle'];
    }

    public get itemPriceFontColor(): CartSettingsFormType['itemPriceFontColor'] {
        return this.itemForm.get('itemPriceFontColor') as CartSettingsFormType['itemPriceFontColor'];
    }

    public get itemTotalPriceFontSize(): CartSettingsFormType['itemTotalPriceFontSize'] {
        return this.itemForm.get('itemTotalPriceFontSize') as CartSettingsFormType['itemTotalPriceFontSize'];
    }

    public get itemTotalPriceFontStyle(): CartSettingsFormType['itemTotalPriceFontStyle'] {
        return this.itemForm.get('itemTotalPriceFontStyle') as CartSettingsFormType['itemTotalPriceFontStyle'];
    }

    public get itemTotalPriceFontColor(): CartSettingsFormType['itemTotalPriceFontColor'] {
        return this.itemForm.get('itemTotalPriceFontColor') as CartSettingsFormType['itemTotalPriceFontColor'];
    }

    public get deleteButtonIconColor(): CartSettingsFormType['deleteButtonIconColor'] {
        return this.itemForm.get('deleteButtonIconColor') as CartSettingsFormType['deleteButtonIconColor'];
    }

    public get deleteButtonBgColor(): CartSettingsFormType['deleteButtonBgColor'] {
        return this.itemForm.get('deleteButtonBgColor') as CartSettingsFormType['deleteButtonBgColor'];
    }

    public get countButtonColor(): CartSettingsFormType['countButtonColor'] {
        return this.itemForm.get('countButtonColor') as CartSettingsFormType['countButtonColor'];
    }

    public get countButtonTextColor(): CartSettingsFormType['countButtonTextColor'] {
        return this.itemForm.get('countButtonTextColor') as CartSettingsFormType['countButtonTextColor'];
    }

    public get countButtonBgColor(): CartSettingsFormType['countButtonBgColor'] {
        return this.itemForm.get('countButtonBgColor') as CartSettingsFormType['countButtonBgColor'];
    }

    public get countButtonBorderColor(): CartSettingsFormType['countButtonBorderColor'] {
        return this.itemForm.get('countButtonBorderColor') as CartSettingsFormType['countButtonBorderColor'];
    }

    public get countButtonRounding(): CartSettingsFormType['countButtonRounding'] {
        return this.itemForm.get('countButtonRounding') as CartSettingsFormType['countButtonRounding'];
    }

    public setCountButtonRounding(value: number): void {
        this.countButtonRounding.setValue(value);
    }

    public setPreviewSize(value: ESize): void {
        this.previewSize.setValue(value);
    }

    public setCountButtonSize(value: ESize): void {
        this.countButtonSize.setValue(value);
    }

    public setIsSeparatedItems(value: boolean): void {
        this.isSeparatedItems.setValue(value);
    }

    public setSeparatorWidth(value: number): void {
        this.separatorWidth.setValue(value);
    }

    public setSeparatorColor({ value, presetName }: ColorPickerEventPayload): void {
        this.separatorColor.setValue(presetName ?? value);
    }

    public setFontStyle(formElement: FormArray, fontStyle: EFontStyle): void {
        const index = formElement.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            formElement.removeAt(index);
        } else {
            formElement.push(this.fb.control(fontStyle));
        }
    }

    public setItemColor({ value, presetName }: ColorPickerEventPayload, formElement: FormControl): void {
        formElement.patchValue(value);
    }

    public setItemFontSize(formElement: FormControl, value: number): void {
        formElement.setValue(value);
    }

    public isActiveTextStyle(formElement: FormArray, fontStyle: EFontStyle): boolean {
        return formElement.value.includes(fontStyle);
    }
}
